/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { DotNetRequestMessageService } from '@mep/web-browser-bridge';
import { Observable, Subject, forkJoin, from, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BaseApiService implements OnDestroy {
  accessToken!: string;
  apiKey!: string;
  userKey!: string;
  headers: any;
  imagesFolder: string = 'sysque';
  messageHandler: DotNetRequestMessageService;
  private readonly closed$ = new Subject<void>();

  constructor(
    private httpClient: HttpClient = inject(HttpClient),
    messageHandler: DotNetRequestMessageService = inject(DotNetRequestMessageService)
  ) {
    this.messageHandler = messageHandler;
    this.initHeaders();
  }

  public ngOnDestroy(): void {
    this.closed$.next();
    this.closed$.complete();
  }

  public getAccessToken(): Observable<string> {
    return from<Promise<string>>(this.messageHandler.sendRequestAsync('GetValidAccessToken'));
  }

  public initHeaders() {
    const accessToken$ = this.getAccessToken().pipe(takeUntil(this.closed$));
    const userKey$ = from(this.messageHandler.sendRequestAsync('GetSysqueUserKey')).pipe(takeUntil(this.closed$));
    const apiKey$ = from(this.messageHandler.sendRequestAsync('GetBuildingDataApiKey')).pipe(takeUntil(this.closed$));
  
    forkJoin([accessToken$, userKey$, apiKey$]).subscribe(([accessToken, userKey, apiKey]) => {
      this.accessToken = accessToken;
      this.userKey = userKey as string;
      this.apiKey = apiKey as string;
      this.headers = this.createBasicHeaders();
    });
  }

  public handleHttpError(reason: any, method: string, href: string): string {
    const errorMessage = `HTTP Exception occurred - HttpError ${reason?.status}\r\nMethod: ${method} - Url: ${href}`;
    const errorMessageFull = `${errorMessage}\r\nReason: ${JSON.stringify(reason)}`;
    return errorMessageFull;
  }

  public handleUndefinedError<TBody>(method: string, href: string, body: TBody | undefined): string {
    const errorMessage = `Exception occurred\r\nMethod: ${method} - Url: ${href}`;
    const errorMessageFull = body ? `${errorMessage}\r\nBody: ${JSON.stringify(body)}` : errorMessage;
    return errorMessageFull;
  }

  public createBasicHeaders(): HttpHeaders {
    this.headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('accept', 'application/json')
      .append('user-Key', this.userKey)
      .append('api-key', this.apiKey)
      .append('Authorization', 'Bearer ' + this.accessToken)
      .append('imagesFolder', this.imagesFolder);

    return this.headers;
  }
  
  public async get<TResult>(href: string, params: HttpParams | null): Promise<TResult> {
    const url = href;
    let result: Promise<TResult | undefined>;

    if (params == null) {
      result = this.httpClient.get<TResult>(url, { headers: this.headers }).toPromise();
    } else {
      result = this.httpClient.get<TResult>(url, { headers: this.headers, params: params }).toPromise();
    }
    const promise = result as Promise<TResult>;
    promise.catch((reason) => this.handleHttpError(reason, 'GET', href));
    return promise;
  }

  /**
   *
   * @param href
   * @param _payload
   * @returns
   */
  public async post<TBody, TResult>(href: string, body: TBody): Promise<TResult | undefined> {
    const url = href;
    const result = await this.httpClient.post<TResult>(url, body, { headers: this.headers });
    const promise = result.toPromise();
    promise.catch((reason) => this.handleHttpError(reason, 'POST', href));
    return promise;
  }

  /**
   *
   * @param href
   * @param body
   * @returns
   */
  public async put<TBody, TResult>(href: string, body: TBody): Promise<TResult> {
    const url = href;
    const headers = this.headers;

    const result: Promise<TResult | undefined> = this.httpClient.put<TResult>(url, body, { headers }).toPromise();
    const promise = result as Promise<TResult>;
    promise.catch((reason) => this.handleHttpError(reason, 'PUT', href));
    return promise;
  }

  /**
   *
   * @param href
   * @param body
   * @returns
   */
  public async patch<TBody, TResult>(href: string, body: TBody): Promise<TResult> {
    const url = href;
    const headers = this.headers;

    const result: Promise<TResult | undefined> = this.httpClient.patch<TResult>(url, body, { headers }).toPromise();
    const promise = result as Promise<TResult>;
    promise.catch((reason) => this.handleHttpError(reason, 'PATCH', href));
    return promise;
  }

  /**
   *
   * @param href
   * @returns
   */
  public async delete<TBody, TResult>(href: string, payload: TBody): Promise<TResult> {
    const url = href;
    let options = {};
    if (payload != null && payload != undefined) {
      options = {
        headers: this.headers,
        body: payload,
      };
    } else {
      options = {
        headers: this.headers,
      };
    }

    const result: Promise<TResult | undefined> = this.httpClient.delete<TResult>(url, options).toPromise();
    const promise = result as Promise<TResult>;
    promise.catch((reason) => this.handleHttpError(reason, 'DELETE', href));

    return promise;
  }
}
